import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
// import GoogleIcon from "@mui/icons-material/Google";
// import FacebookIcon from "@mui/icons-material/Facebook";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { Link, useNavigate, useLocation } from "react-router-dom";
import config from "../config.js";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setCredentials } from "../features/counter/Authslice.js";
import { jwtDecode } from "jwt-decode";

const SignupComponent = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState("signup");
  const [signupData, setSignupData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { API_BASE_URL } = config;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const token = query.get("token");

    if (token) {
      handleSocialLoginSuccess(token);
    }

    const message = location.state?.message;
    if (message) {
      setError({ severity: "success", message });
    }
  }, [location]);

  const handleSocialLoginSuccess = (token) => {
    try {
      localStorage.setItem("accessToken", token);

      const decodedToken = jwtDecode(token);
      const user = {
        id: decodedToken.id,
        email: decodedToken.email,
        role: decodedToken.role,
        username: decodedToken.username || null,
      };

      dispatch(
        setCredentials({
          token,
          user,
          isAuthenticated: true,
        })
      );

      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      const roleRoutes = {
        admin: "/superadmin",
        ca: "/Cadashboard",
        cs: "/csdashboard",
        user: "/dashboard",
      };

      const targetRoute = roleRoutes[user.role] || "/KycManagement";
      navigate(targetRoute);
    } catch (error) {
      console.error("Error processing social login token:", error);
      setError({
        severity: "error",
        message: "Failed to process authentication. Please try again.",
      });
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: "",
      email: "",
      password: "",
      phone: "",
      role: "user",
    },
  });

  // const handleSocialLogin = (provider) => {
  //   window.location.href = `${API_BASE_URL}/api/auth/${provider}`;
  // };

  const onSubmit = async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/signup`, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });

 
      setSignupData(data);
      setStep("verify-otp");
    } catch (err) {
      console.error(
        "Signup error:",
        err.response ? err.response.data : err.message
      );
      setError({
        severity: "error",
        message:
          err.response?.data?.message || "Signup failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const otp = e.target.elements.otp.value;

    try {
      const verificationData = {
        email: signupData.email,
        otp,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/v1/create-user`,
        verificationData
      );

     
      navigate("/login", {
        state: { message: "Account created successfully. Please login." },
      });
    } catch (err) {
      console.error("OTP Verification error:", err.response?.data);
      setError({
        severity: "error",
        message:
          err.response?.data?.message ||
          "OTP Verification failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: "user", label: "User" },
    { value: "ca", label: "CA" },
    { value: "cs", label: "CS" },
    // { value: "admin", label: "Admin" },
    { value: "employee", label: "Employee" },
    { value: "lawyer", label: "Lawyer" },
    { value: "company", label: "Company" },
    { value: "LLP", label: "LLP" },
  ];

  const renderSignupForm = () => (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        {error && (
          <Alert severity={error.severity || "error"} sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}

        <Controller
          name="username"
          control={control}
          rules={{
            required: "Username is required",
            minLength: {
              value: 3,
              message: "Username must be at least 3 characters",
            },
            pattern: {
              value: /^[a-zA-Z0-9_]+$/,
              message:
                "Username can only contain letters, numbers, and underscores",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Username"
              variant="outlined"
              margin="normal"
              error={!!errors.username}
              helperText={errors.username?.message}
              autoComplete="username"
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          rules={{
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Email"
              variant="outlined"
              margin="normal"
              error={!!errors.email}
              helperText={errors.email?.message}
              autoComplete="email"
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          rules={{
            required: "Password is required",
            minLength: {
              value: 8,
              message: "Password must be at least 8 characters",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              margin="normal"
              error={!!errors.password}
              helperText={errors.password?.message}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        <Controller
          name="phone"
          control={control}
          rules={{
            required: "Phone number is required",
            pattern: {
              value: /^[+]?[\d\s\-\(\)]{10,}$/,
              message: "Please enter a valid phone number",
            },
            minLength: {
              value: 10,
              message: "Phone number must be at least 10 digits",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Phone Number"
              variant="outlined"
              margin="normal"
              error={!!errors.phone}
              helperText={errors.phone?.message}
              autoComplete="tel"
              placeholder="+1234567890"
            />
          )}
        />

        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth variant="outlined" margin="normal">
              <InputLabel>Role</InputLabel>
              <Select {...field} label="Role">
                {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{
            mt: 2,
            py: 1.5,
            color: "black",
            backgroundColor: "#EB5A3C",
            "&:hover": {
              backgroundColor: "#ca4e34",
            },
          }}
          disabled={loading}
        >
          {loading ? "Signing Up..." : "Sign Up"}
        </Button>
      </form>

      {/* Social Signup Options
      <Box sx={{ mt: 3, mb: 2 }}>
        <Divider>
          <Typography variant="body2" color="textSecondary">
            OR SIGN UP WITH
          </Typography>
        </Divider>
      </Box> */}

      {/* <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={() => handleSocialLogin("google")}
          sx={{
            py: 1,
            borderColor: "#DB4437",
            color: "#DB4437",
            "&:hover": {
              borderColor: "#DB4437",
              backgroundColor: "rgba(219, 68, 55, 0.04)",
            },
          }}
        >
          Google
        </Button>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<FacebookIcon />}
          onClick={() => handleSocialLogin("facebook")}
          sx={{
            py: 1,
            borderColor: "#4267B2",
            color: "#4267B2",
            "&:hover": {
              borderColor: "#4267B2",
              backgroundColor: "rgba(66, 103, 178, 0.04)",
            },
          }}
        >
          Facebook
        </Button>
      </Box> */}
    </>
  );

  const renderOTPVerification = () => (
    <form onSubmit={handleOTPVerification}>
      {error && (
        <Alert severity={error.severity || "error"} sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <TextField
        name="otp"
        fullWidth
        label="Enter OTP"
        variant="outlined"
        margin="normal"
        helperText="Check your email for the OTP"
        inputProps={{
          pattern: "[0-9]{4}",
          title: "OTP must be 4 digits",
        }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{
          mt: 2,
          py: 1.5,
          color: "black",
          backgroundColor: "#EB5A3C",
          "&:hover": {
            backgroundColor: "#ca4e34",
          },
        }}
        disabled={loading}
      >
        {loading ? "Verifying..." : "Verify OTP"}
      </Button>
    </form>
  );

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Paper
        elevation={12}
        sx={{
          p: 4,
          borderRadius: 3,
          textAlign: "center",
        }}
        component={motion.div}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Box>
          <Typography>Start your journey with us</Typography>
        </Box>

        <Typography variant="h4" gutterBottom>
          {step === "signup" ? "Create Account" : "Verify OTP"}
        </Typography>

        {step === "signup" ? renderSignupForm() : renderOTPVerification()}

        {step === "signup" && (
          <Box mt={2}>
            <Typography variant="body2">
              Already have an account? <Link to="/login">Sign In</Link>
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SignupComponent;
